import { z } from 'zod';
/**
 * Tenant branding (task 97, decision 0073). A tenant picks one colour for its own app; the platform tenant
 * sets the defaults: one for insurers, brokers and itself, one for business owners. Nothing else is
 * brandable: score bands and good/warning/critical keep their colours, so a score reads the same in every
 * tenant, on public badges and in insurer reports.
 *
 * Every shade the app draws is derived from the one colour, so nobody has to pick twelve. A colour too pale
 * for white text is darkened for buttons and links until it reads at 4.5:1 (WCAG AA), and only the tints
 * keep the colour as picked.
 */
export const DEFAULT_BRAND = {
    /** Insurers, brokers and the platform: the Wersel green (decision 0075). */
    staff: '#65b17f',
    /** Business owners: the same green; the platform can still give them a colour of their own. */
    owner: '#65b17f',
};
export const HEX_COLOUR = /^#[0-9a-f]{6}$/;
const Hex = z.string().trim().toLowerCase().regex(HEX_COLOUR, 'Use a colour like #65b17f');
/** null puts a colour back to the default. Only the platform sets the business owners' colour. */
export const BrandingPatch = z
    .strictObject({ colour: Hex.nullable().optional(), owner_colour: Hex.nullable().optional() })
    .refine((b) => b.colour !== undefined || b.owner_colour !== undefined, 'Change a colour');
export function rgbOf(hex) {
    if (!HEX_COLOUR.test(hex.toLowerCase()))
        return null;
    const n = parseInt(hex.slice(1), 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}
const hexOf = (c) => '#' +
    c
        .map((v) => Math.round(Math.max(0, Math.min(255, v)))
        .toString(16)
        .padStart(2, '0'))
        .join('');
function luminance([r, g, b]) {
    const ch = (v) => {
        const s = v / 255;
        return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
    };
    return 0.2126 * ch(r) + 0.7152 * ch(g) + 0.0722 * ch(b);
}
/** WCAG contrast ratio between two colours. */
export function contrast(a, b) {
    const [x, y] = [rgbOf(a), rgbOf(b)];
    if (!x || !y)
        return 1;
    const [hi, lo] = [luminance(x), luminance(y)].sort((p, q) => q - p);
    return (hi + 0.05) / (lo + 0.05);
}
/** Lighten or darken by `amount` (-1..1) in HSL lightness, keeping hue and saturation (customer360's ramp). */
export function adjustLightness(hex, amount) {
    const rgb = rgbOf(hex);
    if (!rgb)
        return hex;
    const [r, g, b] = rgb.map((v) => v / 255);
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const l = (max + min) / 2;
    let h = 0;
    let s = 0;
    if (max !== min) {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        h = max === r ? (g - b) / d + (g < b ? 6 : 0) : max === g ? (b - r) / d + 2 : (r - g) / d + 4;
        h /= 6;
    }
    const nl = Math.max(0, Math.min(1, l + amount));
    const hue = (p, q, t) => {
        if (t < 0)
            t += 1;
        if (t > 1)
            t -= 1;
        if (t < 1 / 6)
            return p + (q - p) * 6 * t;
        if (t < 1 / 2)
            return q;
        if (t < 2 / 3)
            return p + (q - p) * (2 / 3 - t) * 6;
        return p;
    };
    const q = nl < 0.5 ? nl * (1 + s) : nl + s - nl * s;
    const p = 2 * nl - q;
    return hexOf([hue(p, q, h + 1 / 3), hue(p, q, h), hue(p, q, h - 1 / 3)].map((v) => v * 255));
}
/** `amount` of `hex` over white (0..1), in sRGB. */
export function tint(hex, amount, over = '#ffffff') {
    const [a, b] = [rgbOf(hex), rgbOf(over)];
    if (!a || !b)
        return hex;
    return hexOf(a.map((v, i) => v * amount + b[i] * (1 - amount)));
}
/**
 * The colour as text and buttons use it: darkened in lightness only until white text on it, and it on
 * white, reads at `target`:1. A colour that already does is returned as picked.
 */
export function readable(hex, target = 4.5) {
    if (!rgbOf(hex))
        return hex;
    let c = hex;
    for (let i = 0; i < 100 && contrast(c, '#ffffff') < target; i++)
        c = adjustLightness(c, -0.01);
    return c;
}
/**
 * The pale tint (75) and the mid shade a step lighter than the colour (200) that the record tabs are drawn
 * from. Some colours have hand-picked ones, like customer360's presets: the Wersel green takes its own
 * ramp, and the earlier teal shares the Lagoon preset's hue, so it takes Lagoon's shades and the tabs match
 * Policy 360's. Any other colour gets customer360's computed ramp.
 */
export const PICKED_SHADES = {
    [DEFAULT_BRAND.staff]: { 75: '#c1e0cc', 200: '#83c098' },
    // The staff app's teal and the owners' blue before decision 0075, for tenants that picked them.
    '#0e4f4c': { 75: '#b3ddd8', 200: '#2e9d93' },
    '#007ef9': { 75: '#b0cce8', 200: '#378fe6' },
    // customer360's Lagoon itself.
    '#127f77': { 75: '#b3ddd8', 200: '#2e9d93' },
};
/**
 * The Wersel green is drawn as the style guide has it (decision 0075): white text on #65b17f and
 * #467b58 on hover, although that is under 4.5:1. Every other colour is made readable as below.
 */
const WERSEL = {
    '--primary': '#65b17f',
    '--primary-hover': '#467b58',
    '--primary-soft': '#e6f7f6',
    '--primary-line': '#83c098',
};
export function brandShades(hex) {
    const c = hex.toLowerCase();
    return PICKED_SHADES[c] ?? { 75: adjustLightness(c, 0.28), 200: adjustLightness(c, 0.08) };
}
/**
 * The CSS custom properties for a colour, for the app that shows it: the staff app's tokens, or the
 * business owners' (which have a few more, for their cards and promotions). The page sets them inline on
 * the app's root, so every screen already reading these tokens follows.
 */
export function brandTokens(hex, app) {
    const c = hex.toLowerCase();
    const wersel = c === DEFAULT_BRAND.staff;
    const safe = wersel ? c : readable(c);
    const shades = brandShades(c);
    const tokens = {
        '--brand': c,
        '--primary': safe,
        '--primary-hover': adjustLightness(safe, -0.06),
        '--primary-soft': tint(c, 0.09),
        '--primary-line': tint(c, 0.3),
        ...(wersel ? WERSEL : {}),
        '--brand-base-75': shades[75],
        '--brand-base-200': shades[200],
    };
    if (app === 'owner')
        Object.assign(tokens, {
            '--primary-600': adjustLightness(safe, -0.08),
            '--primary-soft-2': tint(c, 0.22),
            '--promo-a': adjustLightness(c, -0.04),
            '--promo-b': adjustLightness(c, 0.08),
            '--tile': tint(c, 0.12),
        });
    return tokens;
}
/** Which app a tenant's people see: business owners theirs, everyone else the staff app. */
export const brandApp = (kind) => (kind === 'business' ? 'owner' : 'staff');
//# sourceMappingURL=branding.js.map